import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Icon, InlineFeedback, LoadingMask, Text, Toast, useToast } from '@zillow/constellation-10';
import { IconFloorPlanOutline } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyFloorPlanCreateV2WebApiHandler_create } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';

import FeedFloorPlanCreateModal from '../../../modals/FeedFloorPlanCreateModal';
import FloorPlanModal from '../../../modals/FloorPlanModal';
import SavedFloorPlanCard from './components/SavedFloorPlanCard';

import { useAuth } from '../../../AuthProvider';
import RecommendationsAlert from '../../../PostingPath/RecommendationsAlert';

import type { DatarouterError } from '@zg-rentals/web-base';
type Props = {
  onRecsClick: (recKey: string) => void;
};

const FloorPlansContent = ({ onRecsClick }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails, isFetching: isFetchingListingDetails } = useGetListingDetails(listingAlias);

  const [floorPlanIndex, setFloorPlanIndex] = useState(0);
  const [displayFloorPlanModal, setDisplayFloorPlanModal] = useState(false);
  const [displayFeedCreateModal, setDisplayFeedCreateModal] = useState(false);

  const floorplans = listingDetails?.floorPlans || [];
  const units = listingDetails?.units || [];

  const isFeed = Boolean(listingDetails?.isFeed);
  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.FLOOR_PLANS);

  const { mutateAsync: handleCreateFloorPlan, isPending: isCreatingFloorPlan } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanCreateV2WebApiHandler_create,
    successToastMessage: 'Floor plan created',
    onSuccess: (response) => {
      if (!response) {
        logError({
          error: new Error('No response from floor plan creation'),
          context: { details: 'FloorPlansContent.createFloorPlan', listingAlias },
        });
        return;
      }

      // Open the newly created floor plan in the modal
      const newFloorPlanIndex = floorplans.length;
      onSuccessCallback(newFloorPlanIndex);
    },
    onError: (error) => {
      enqueueToast(
        <Toast tone="critical">
          {(error as DatarouterError)?.message || 'Floor plan couldn’t be created. Try again.'}
        </Toast>,
      );
    },
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'RentalManagerUserPropertyFloorPlanCreateV2WebApiHandler_create', details: 'create floor plan' },
    },
  });

  // Failures are toasted by the mutation's onError, so the rejection has no other listener.
  const createFloorPlan = () => void handleCreateFloorPlan({ postData: { listingAlias } }).catch(() => undefined);

  const isOnlyFloorPlanNotStarted =
    floorplans.length <= 1 &&
    !floorplans[0]?.name &&
    !(floorplans[0]?.numBedrooms || floorplans[0]?.numBedrooms === 0) &&
    !(floorplans[0]?.numBathrooms || floorplans[0]?.numBathrooms === 0) &&
    !floorplans[0]?.squareFeet &&
    !floorplans[0]?.lowPrice &&
    !floorplans[0]?.highPrice &&
    !floorplans[0]?.active;

  const isAtLeastOneFloorPlanIncomplete = floorplans.some(
    (floorPlan) =>
      !floorPlan.name ||
      !(floorPlan.numBedrooms || floorPlan.numBedrooms === 0) ||
      !(floorPlan.numBathrooms || floorPlan.numBathrooms === 0),
  );

  const onSuccessCallback = (fpIndex = 0) => {
    setFloorPlanIndex(fpIndex);
    setDisplayFloorPlanModal(true);
  };

  return (
    <>
      <LoadingMask.Root loading={isFetchingListingDetails} css={{ width: '100%' }}>
        <LoadingMask.Content css={{ width: '100%' }}>
          <Box data-testid="FloorPlansContent-Edit" css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
            {isOnlyFloorPlanNotStarted && units.length === 0 ? (
              <Box
                css={{
                  minHeight: '240px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '400',
                  flex: '1',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Icon size="md" render={<IconFloorPlanOutline />} />

                <Text>No floor plans added</Text>

                {!isInputFormDisabled && (
                  <Box>
                    <Button
                      disabled={isCreatingFloorPlan}
                      onClick={() => {
                        if (isFeed) {
                          setDisplayFeedCreateModal(true);
                        } else if (!floorplans[0]) {
                          createFloorPlan();
                        } else {
                          onSuccessCallback();
                        }
                      }}
                    >
                      Add floor plan
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                <RecommendationsAlert sectionName={REVIEW_PAGE_SECTIONS.FLOOR_PLANS} onRecsClick={onRecsClick} />

                <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
                  {floorplans.map((floorPlan, index) => (
                    <SavedFloorPlanCard
                      key={`floorPlanCard-${floorPlan.id}`}
                      floorPlan={floorPlan}
                      floorPlanIndex={index}
                    />
                  ))}

                  {units.length > 0 && (
                    <SavedFloorPlanCard
                      key="units-without-floorPlanCard"
                      floorPlan={{
                        units: units,
                      }}
                    />
                  )}
                </Box>

                {!isInputFormDisabled && (
                  <Box>
                    <Button
                      loading={!isFeed && isCreatingFloorPlan}
                      disabled={!isFeed && isAtLeastOneFloorPlanIncomplete}
                      onClick={() => (isFeed ? setDisplayFeedCreateModal(true) : createFloorPlan())}
                      tone="brand"
                      emphasis="filled"
                    >
                      Add another floor plan
                    </Button>
                    {!isFeed && isAtLeastOneFloorPlanIncomplete && (
                      <InlineFeedback tone="critical" css={{ marginTop: '100', display: 'flex' }}>
                        At least one added floor plan is missing required information. You must correct or delete that
                        floor plan before adding another.
                      </InlineFeedback>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </LoadingMask.Content>
        <LoadingMask.Spinner />
      </LoadingMask.Root>

      {Boolean(displayFloorPlanModal && floorplans[floorPlanIndex]) && (
        <FloorPlanModal floorPlanIndex={floorPlanIndex} onClose={() => setDisplayFloorPlanModal(false)} />
      )}

      {displayFeedCreateModal && (
        <FeedFloorPlanCreateModal listingAlias={listingAlias} onClose={() => setDisplayFeedCreateModal(false)} />
      )}
    </>
  );
};

export default memo(FloorPlansContent);
